import React, { useMemo, useState } from 'react';
import { Button } from '../../ui/Button';

const field = 'w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-meetoi-navy';

const detail = (c) => [c.company, c.email].filter(Boolean).join(' · ');

export const ContactPicker = ({ contacts, value, onChange, candidateIds, personName, onCreate }) => {
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const selected = contacts.find((c) => c.id === value);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const shown = contacts.filter((c) => !q || `${c.full_name} ${c.company || ''} ${c.email || ''}`.toLowerCase().includes(q));
    const rank = (c) => (candidateIds.includes(c.id) ? 0 : 1);
    return [...shown].sort((a, b) => rank(a) - rank(b)).slice(0, 8);
  }, [contacts, search, candidateIds]);

  const create = async (name) => {
    if (creating) return;
    setCreating(true);
    setError('');
    try {
      await onCreate(name);
      setSearch('');
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  if (selected) {
    return (
      <div className="mt-1 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
        <span>
          <span className="font-medium text-slate-900">{selected.full_name}</span>
          {detail(selected) && <span className="block text-xs text-slate-500">{detail(selected)}</span>}
        </span>
        <button type="button" className="text-xs font-medium text-violet-700 hover:underline" onClick={() => onChange('')}>Change</button>
      </div>
    );
  }

  const hint = candidateIds.length > 1
    ? `Several contacts match “${personName}”. Choose the right one.`
    : personName
      ? `No contact matches “${personName}”. Search for someone else or create a new contact.`
      : 'Choose who this follow-up is about.';

  return (
    <div className="mt-1 space-y-2">
      <p className="text-xs text-amber-700">{hint}</p>
      <input className={field} value={search} placeholder="Search contacts by name, company or email" onChange={(e) => setSearch(e.target.value)} />
      <ul className="max-h-48 overflow-auto rounded-xl border border-slate-200 bg-white">
        {list.length === 0 && <li className="px-3 py-2 text-xs text-slate-500">No matching contacts.</li>}
        {list.map((c) => (
          <li key={c.id}>
            <button type="button" className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-50" onClick={() => onChange(c.id)}>
              <span className="font-medium text-slate-900">{c.full_name}</span>
              {detail(c) && <span className="block text-xs text-slate-500">{detail(c)}</span>}
            </button>
          </li>
        ))}
      </ul>
      {(personName || search.trim()) && (
        <Button variant="secondary" isLoading={creating} onClick={() => create(search.trim() || personName)}>
          Create new contact “{search.trim() || personName}”
        </Button>
      )}
      <p className="text-xs text-slate-500">A follow-up has to belong to a person. Creating a contact does not make them a Connection.</p>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};